import { entryKeys, tagKeys } from '@taler/api-client'
import type { LocalDate } from '@taler/domain'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef, ref, type Ref } from 'vue'
import { useApi } from '@/app/api'
import {
  createGenericEntry,
  toPostEntryInput,
  validateGenericEntry,
  type GenericEntryDraft,
  type GenericValidation,
} from '../internal/generic-draft.ts'
import { prefersReducedMotion } from '../internal/motion.ts'
import { problemDetail } from '../internal/problem.ts'
import type { Target } from '../internal/targets.ts'

export interface UseGenericEntryOptions {
  readonly ledgerId: () => string | undefined
  readonly targetsById: () => ReadonlyMap<string, Target>
  readonly today: () => LocalDate
}

export interface UseGenericEntry {
  readonly entry: Ref<GenericEntryDraft>
  readonly validation: ComputedRef<GenericValidation>
  readonly isSubmitting: ComputedRef<boolean>
  readonly submitError: ComputedRef<Error | null>
  readonly submitErrorDetail: ComputedRef<string | undefined>
  readonly justPosted: Ref<boolean>
  addLine(): void
  removeLine(lineId: string): void
  submit(): Promise<void>
}

export function useGenericEntry(options: UseGenericEntryOptions): UseGenericEntry {
  const api = useApi()
  const queryClient = useQueryClient()
  const entry = ref<GenericEntryDraft>(createGenericEntry(options.today())) as Ref<GenericEntryDraft>
  const justPosted = ref(false)
  let justPostedTimer: ReturnType<typeof setTimeout> | undefined

  const validation = computed(() => validateGenericEntry(entry.value, options.targetsById()))

  const submitMutation = useMutation({
    mutationFn: async () => {
      const input = toPostEntryInput(entry.value, options.targetsById())
      if (input === undefined) throw new Error('The entry is not ready to post')
      return api.entries.batchCreate(options.ledgerId()!, [input])
    },
    onSuccess: () => {
      justPosted.value = true
      entry.value = createGenericEntry(options.today())
      for (const key of entryKeys.invalidatedByWrite(options.ledgerId() ?? '')) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
      void queryClient.invalidateQueries({ queryKey: tagKeys.list(options.ledgerId() ?? '') })
      if (justPostedTimer !== undefined) clearTimeout(justPostedTimer)
      justPostedTimer = setTimeout(
        () => {
          justPosted.value = false
        },
        prefersReducedMotion() ? 300 : 1000,
      )
    },
  })

  return {
    entry,
    validation,
    isSubmitting: computed(() => submitMutation.isPending.value),
    submitError: computed(() => submitMutation.error.value),
    submitErrorDetail: computed(() => problemDetail(submitMutation.error.value)),
    justPosted,
    addLine: () => {
      entry.value.lines.push({ id: crypto.randomUUID(), targetId: undefined, amount: null, memo: '' })
    },
    removeLine: (lineId) => {
      if (entry.value.lines.length <= 2) return
      entry.value.lines = entry.value.lines.filter((line) => line.id !== lineId)
    },
    // The mutation's own `error` ref is what the UI reads; this promise is not
    // awaited by any caller, so a rejection here must not become an unhandled one.
    submit: () =>
      submitMutation.mutateAsync().then(
        () => undefined,
        () => undefined,
      ),
  }
}
